/**
 * Recovery for the deploy-vs-open-tab race.
 *
 * Pages are code-split (React.lazy), so each page's JS is a separate hashed
 * file. When a new version deploys, the old hashes vanish from the server and
 * the static-site catch-all answers with index.html (text/html) instead. A tab
 * that was open across the deploy then fails on its next page switch with
 * "'text/html' is not a valid JavaScript MIME type" (Safari) or
 * "Failed to fetch dynamically imported module" (Chrome). A full reload always
 * fixes it, because the fresh index.html references the current hashes.
 *
 * These helpers perform that reload automatically — at most once per
 * RELOAD_WINDOW_MS, so a genuinely broken bundle still surfaces the
 * ErrorBoundary card instead of reload-looping.
 *
 * @module utils/staleChunkReload
 */

const RELOAD_GUARD_KEY = 'ballast:stale-chunk-reload-at';
const RELOAD_WINDOW_MS = 30_000;

/** Matches Safari, Chrome, and Firefox wordings of a failed chunk load. */
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /is not a valid JavaScript MIME type|dynamically imported module|Importing a module script failed|Unable to preload CSS/i
      .test(message)
  );
}

/**
 * Reload the page to pick up the freshly deployed build.
 *
 * Returns true if a reload was initiated, false if one already happened
 * within the guard window (sessionStorage — per-tab, survives the reload).
 */
export function reloadOnceForStaleChunk(): boolean {
  let lastReloadAt = 0;
  try {
    lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
  } catch {
    // sessionStorage unavailable (privacy mode) — still reload; without the
    // guard readable we prefer one possible extra reload over a dead end.
  }
  if (Date.now() - lastReloadAt < RELOAD_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    /* see above */
  }
  window.location.reload();
  return true;
}
